import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import { SpotifyConnectionBannerStyle } from './SpotifyConnectionBanner.style';
import { useLoginContext } from '../../context';
import { SpotifyReconnectModal } from '../SpotifyReconnectModal';
import {
  buildSpotifyAuthorizeUrl,
  getReconnectDisplayName,
  needsSpotifyReconnect,
  resolveReconnectIdentity,
  SPOTIFY_REAUTH_EVENT,
  takeSpotifyReconnectNotice,
  SpotifyProfileIdentity,
} from '../../utils/spotifyAuth';

const SpotifyConnectionBanner = () => {
  const { state: loginState } = useLoginContext();
  const [showBanner, setShowBanner] = useState(false);
  const [isReconnect, setIsReconnect] = useState(false);
  const [profileName, setProfileName] = useState('Spotify');
  const [reconnectModalOpen, setReconnectModalOpen] = useState(false);
  const [pendingIdentity, setPendingIdentity] = useState<SpotifyProfileIdentity | null>(null);

  const recompute = () => {
    // Active profile only — never show Farhan reconnect while session is on UM.
    const reconnectFromStorage = needsSpotifyReconnect();
    const activeUser = (() => {
      try {
        return JSON.parse(localStorage.getItem('spotifyUser') || 'null');
      } catch {
        return null;
      }
    })();
    const loginReconnectMatchesActive =
      loginState?.spotifyNeedsReconnect === true &&
      (!loginState?.reconnectProfile?.profileId ||
        !activeUser?.profileId ||
        String(loginState.reconnectProfile.profileId) === String(activeUser.profileId));
    const reconnect = reconnectFromStorage || loginReconnectMatchesActive;
    const spotifyUser = localStorage.getItem('spotifyUser');
    const hasConnection = localStorage.getItem('hasSpotifyConnection') === 'true';
    const bannerDismissed = localStorage.getItem('spotifyBannerDismissed') === 'true';
    const identity = resolveReconnectIdentity(loginState?.reconnectProfile || null);
    const name = identity?.username || getReconnectDisplayName();
    setProfileName(name);
    setIsReconnect(reconnect);

    if (reconnect) {
      setShowBanner(true);
      return;
    }

    // First-connect: show when not connected (unless dismissed)
    setShowBanner(!spotifyUser && !hasConnection && !bannerDismissed);
  };

  useEffect(() => {
    recompute();
    const reconnectNotice = takeSpotifyReconnectNotice();
    if (reconnectNotice) {
      // Longer duration for actionable recovery messages (e.g. revoke app access).
      message.warning(reconnectNotice, 16);
    }
    const onReauth = () => recompute();
    window.addEventListener(SPOTIFY_REAUTH_EVENT, onReauth);
    return () => window.removeEventListener(SPOTIFY_REAUTH_EVENT, onReauth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginState?.spotifyNeedsReconnect]);

  const startSpotifyOAuth = (mode: 'reconnect' | 'first_connect', identity: SpotifyProfileIdentity | null) => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      if (userInfo?.campaignsOnboardingRequired === true) {
        localStorage.setItem('postSpotifyRedirect', '/campaigns?onboarding=1');
      }
    } catch {
      // ignore
    }

    window.location.replace(
      buildSpotifyAuthorizeUrl({
        showDialog: true,
        mode,
        identity: mode === 'reconnect' ? identity : null,
      })
    );
  };

  const handleConnectSpotify = () => {
    const identity = resolveReconnectIdentity(loginState?.reconnectProfile || null);
    const shared = identity?.isShared === true;

    if (shared && isReconnect) {
      message.warning(
        'This Spotify account’s connection expired. Ask the account owner to reconnect Spotify in Songplace.'
      );
      return;
    }

    if (isReconnect) {
      setPendingIdentity(identity);
      setReconnectModalOpen(true);
      return;
    }

    startSpotifyOAuth('first_connect', null);
  };

  const handleConfirmReconnect = () => {
    setReconnectModalOpen(false);
    startSpotifyOAuth('reconnect', pendingIdentity);
  };

  const handleDismiss = () => {
    if (isReconnect) {
      // Sticky for reconnect — keep banner visible
      return;
    }
    setShowBanner(false);
    localStorage.setItem('spotifyBannerDismissed', 'true');
  };

  if (!showBanner && !reconnectModalOpen) return null;

  return (
    <>
      {showBanner ? (
        <SpotifyConnectionBannerStyle>
          <div className="spotify-connection-banner">
            <div className="banner-content">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="white"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
              </svg>
              <span>
                {isReconnect
                  ? `Your Spotify connection for ${profileName} expired. Spotify requires re-authorization every 6 months. Reconnect to restore playlists and scheduled actions. Your Songplace data is safe.`
                  : 'Connect your Spotify account to manage playlists and schedule songs'}
              </span>
            </div>
            <div className="banner-actions">
              <button onClick={handleConnectSpotify} className="connect-btn">
                {isReconnect ? `Reconnect ${profileName}` : 'Connect Spotify'}
              </button>
              {!isReconnect && (
                <button onClick={handleDismiss} className="dismiss-btn">
                  Later
                </button>
              )}
            </div>
          </div>
        </SpotifyConnectionBannerStyle>
      ) : null}

      <SpotifyReconnectModal
        visible={reconnectModalOpen}
        identity={pendingIdentity}
        onCancel={() => setReconnectModalOpen(false)}
        onConfirm={handleConfirmReconnect}
      />
    </>
  );
};

export default SpotifyConnectionBanner;
